package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlin.time.Instant

/*
 * SH-126: training and onboarding courses with a quiz, and the OHS training record.
 *
 * Where it lives (docs/training.md, docs/compliance/gdpr-engineering.md, "Training"):
 * - workplaces/{wpId}/courses/{courseId}: the course ([Course]): pages, questions without the
 *   answers, who it is for. Everyone working there reads it; managers write it.
 * - workplaces/{wpId}/course_keys/{courseId}: the right answers ([CourseKey]). Managers only, so
 *   the answers never reach an employee's device; functions/courses.js grades on the server.
 * - workplaces/{wpId}/courses/{id}/assignments/{uid}: who a manager asked to take it, by when.
 * - workplaces/{wpId}/course_progress/{uid}_{courseId}: the pages a person has seen (their own).
 * - workplaces/{wpId}/courses/{id}/completions/{uid}: the result ([CourseCompletion]), written only
 *   by the submitCourse function with the server's time: the version taken, score, attempts.
 * - workplaces/{wpId}/ohs_records/{uid}: the person's OHS training record ([OhsRecord]), the
 *   electronic side of the karta szkolenia wstępnego and the periodic training: server only.
 * - workplaces/{wpId}/training_status/{uid}: done / pending / overdue for the assigned courses
 *   that count for readiness ([TrainingStatus]), server only.
 *
 * The law (rozporządzenie MGiP z 27.07.2004 w sprawie szkolenia w dziedzinie BHP, Dz.U. 2024
 * poz. 1327 as amended by Dz.U. 2025 poz. 1640; docs/training.md has the sources):
 * - general instruction (instruktaż ogólny, § 8–10) may be taken in the app ([CourseKind.OHS_GENERAL]),
 *   with a named person conducting it and the employee's electronic confirmation (§ 12 ust. 1);
 * - workplace instruction (instruktaż stanowiskowy, § 11) happens at the workplace: the app only
 *   records it and the confirmations of the person and the head of the unit;
 * - periodic training as guided self-study (samokształcenie kierowane, § 15 ust. 2) for everyone
 *   except blue-collar positions ([CourseKind.OHS_PERIODIC]); it ends with an exam before a
 *   commission (§ 16) that a manager records, then a certificate (zaświadczenie) is issued.
 */

/** What a course is, and so which rules apply to it. */
enum class CourseKind {
    /** Any onboarding or product course: no legal rules. */
    GENERAL,
    /** OHS general instruction (instruktaż ogólny, § 8–10 of the regulation). */
    OHS_GENERAL,
    /** OHS periodic training as guided self-study (samokształcenie kierowane, § 15 ust. 2). */
    OHS_PERIODIC;

    val isOhs: Boolean get() = this != GENERAL
}

/**
 * SH-211 added [PRESENTATION]: older app versions read an unknown kind as [TEXT] and show the
 * page's plain `body` (CourseMapper), which then names the presentation's address.
 */
enum class CoursePageKind { TEXT, IMAGE, VIDEO_LINK, PRESENTATION }

enum class CourseStatus { DRAFT, PUBLISHED }

/**
 * One page of a course. A video is a link the device opens outside the app (privacy note).
 * SH-211: [rich] is the formatted lesson ([RichDocument]); [body] then holds its plain-text
 * version ([CourseRules.forStorage]), which older app versions show. A lesson without
 * formatting keeps only [body], as before.
 */
data class CoursePage(
    val id: String,
    val kind: CoursePageKind = CoursePageKind.TEXT,
    val title: String = "",
    val body: String = "",
    /** IMAGE: the picture in Storage ([CourseFiles.imagePath]). */
    val imagePath: String? = null,
    /** VIDEO_LINK: an https address. */
    val url: String? = null,
    /** The formatted lesson text; null: [body] is plain text. */
    val rich: RichDocument? = null,
    /** PRESENTATION: the link or the uploaded file. */
    val presentation: CoursePresentation? = null
) {
    /** The lesson text to edit or show: [rich], or [body] read as plain paragraphs. */
    val document: RichDocument get() = rich ?: RichDocument.plain(body)
}

/** A question with 2–6 options; [multi]: more than one may be right (all of them must be picked). */
data class QuizQuestion(
    val id: String,
    val text: String,
    val options: List<String>,
    val multi: Boolean = false
)

/** OHS courses: the details the regulation asks for. */
data class OhsCourseInfo(
    /** The programme's length in lesson hours of 45 minutes (załącznik nr 1). */
    val lessonHours: Int = 0,
    /** Who conducts the instruction or takes consultations (§ 10 ust. 2, § 1a pkt 5). */
    val instructorName: String = "",
    /** How to reach them for questions (consultations during self-study). */
    val consultation: String = ""
)

data class Course(
    val id: String,
    val workplaceId: String,
    val title: String,
    val description: String = "",
    val kind: CourseKind = CourseKind.GENERAL,
    val pages: List<CoursePage> = emptyList(),
    val questions: List<QuizQuestion> = emptyList(),
    /** The share of right answers needed to pass, 50–100. */
    val passPercent: Int = CourseRules.DEFAULT_PASS_PERCENT,
    /** Everyone in these positions takes it. */
    val roles: List<String> = emptyList(),
    /** Everyone who starts working here after it was published takes it. */
    val allNewHires: Boolean = false,
    /** Days to finish it, from the assignment (or the start of work for new hires). */
    val dueDays: Int = CourseRules.DEFAULT_DUE_DAYS,
    /** Counts for the person's training status (OHS courses always do). */
    val countsForReadiness: Boolean = false,
    val ohs: OhsCourseInfo? = null,
    /** Raised on every publish; results keep the version they were for. */
    val version: Int = 0,
    val status: CourseStatus = CourseStatus.DRAFT,
    val publishedAt: Instant? = null,
    val updatedAt: Instant? = null,
    val updatedBy: String? = null
) {
    val hasQuiz: Boolean get() = questions.isNotEmpty()
    val isPublished: Boolean get() = status == CourseStatus.PUBLISHED
}

/** The right answers: question id → the indexes of the right options. Managers only. */
data class CourseKey(
    val courseId: String,
    val version: Int,
    val correct: Map<String, List<Int>>
)

/** A manager asked [userId] to take a course by [dueOn]. */
data class CourseAssignment(
    val courseId: String,
    val userId: String,
    val dueOn: LocalDate? = null,
    val assignedAt: Instant? = null,
    val assignedBy: String? = null
)

/** The pages a person has seen (their own record). */
data class CourseProgress(
    val courseId: String,
    val userId: String,
    val pagesSeen: List<String> = emptyList(),
    val startedAt: Instant? = null,
    val updatedAt: Instant? = null
)

/** The server's record of a person's result (functions/courses.js). */
data class CourseCompletion(
    val courseId: String,
    val userId: String,
    val version: Int,
    val score: Int,
    val passed: Boolean,
    val attempts: Int,
    /** Attempts on [attemptDay] (Warsaw), at most [CourseRules.MAX_ATTEMPTS_PER_DAY]. */
    val attemptsToday: Int = 0,
    val attemptDay: LocalDate? = null,
    /** The first passing attempt (the server's clock). */
    val completedAt: Instant? = null,
    val lastAttemptAt: Instant? = null,
    val displayName: String? = null
) {
    fun attemptsLeftOn(day: LocalDate): Int =
        if (attemptDay == day) (CourseRules.MAX_ATTEMPTS_PER_DAY - attemptsToday).coerceAtLeast(0) else CourseRules.MAX_ATTEMPTS_PER_DAY
}

/** What the server answered to a submitted quiz. */
data class CourseResult(
    val completion: CourseCompletion,
    /** The question ids answered wrongly this time (not which answer is right). */
    val wrongQuestionIds: List<String> = emptyList(),
    /** The OHS record after an OHS course, when it changed. */
    val ohsRecord: OhsRecord? = null
)

/** Why the server refused a training action (functions/courses.js details.reason). */
enum class CourseFailure {
    /** The course was published again while the person took it. */
    COURSE_CHANGED,
    /** [CourseRules.MAX_ATTEMPTS_PER_DAY] used today. */
    NO_ATTEMPTS_LEFT,
    /** The OHS general instruction needs the employee's confirmation. */
    CONFIRMATION_MISSING,
    /** Guided self-study is not for blue-collar positions. */
    SELF_STUDY_NOT_ALLOWED,
    /** The exam needs a passed self-study test first. */
    TEST_NOT_PASSED,
    NOTHING_TO_CONFIRM,
    /** The document cannot be printed yet. */
    NOT_READY,
    /** The workplace's plan has ended. */
    PLAN_ENDED,
    /** SH-244: the owner is the employer and has no initial OHS training record. */
    OWNER_NOT_TRAINED
}

class CourseActionException(val failure: CourseFailure) : IllegalStateException("Training action refused: $failure")

/** Why a course cannot be saved or published. */
enum class CourseProblem {
    MISSING_TITLE,
    TITLE_TOO_LONG,
    NO_PAGES,
    TOO_MANY_PAGES,
    PAGE_TOO_LONG,
    /** A video page without an https address. */
    BAD_VIDEO_LINK,
    /** An image page without a picture. */
    MISSING_IMAGE,
    /** SH-211: a presentation page without a link or a file. */
    BAD_PRESENTATION,
    /** SH-211: the course would not fit in one Firestore document with room to spare. */
    COURSE_TOO_LARGE,
    TOO_MANY_QUESTIONS,
    QUESTION_WITHOUT_TEXT,
    /** Fewer than 2 or more than 6 options, or an empty one. */
    BAD_OPTIONS,
    /** A question without a right answer, or a single-answer question with several. */
    NO_RIGHT_ANSWER,
    BAD_PASS_PERCENT,
    BAD_DUE_DAYS,
    /** OHS courses end with a test (§ 11 ust. 6, § 16). */
    OHS_NEEDS_QUIZ,
    /** The person conducting the instruction or taking consultations is not named. */
    OHS_NEEDS_INSTRUCTOR,
    /** Shorter than the framework programme's minimum ([CourseRules.minLessonHours]). */
    OHS_TOO_SHORT
}

class InvalidCourseException(val problems: Set<CourseProblem>) : IllegalArgumentException("Invalid course: $problems")

/** Course limits, shared with firestore.rules (validCourse) and functions/courses.js: keep them in step. */
object CourseRules {
    const val MAX_TITLE = 120
    const val MAX_DESCRIPTION = 1000
    const val MAX_PAGES = 30
    const val MAX_PAGE_TITLE = 120
    const val MAX_BODY = 4000
    const val MAX_URL = 500
    const val MAX_QUESTIONS = 20
    const val MAX_QUESTION = 300
    const val MIN_OPTIONS = 2
    const val MAX_OPTIONS = 6
    const val MAX_OPTION = 200
    const val MAX_ROLES = 20
    const val MAX_OHS_TEXT = 120
    const val DEFAULT_PASS_PERCENT = 80
    const val DEFAULT_DUE_DAYS = 14
    const val MAX_DUE_DAYS = 365
    /**
     * SH-211: the whole course in UTF-8 bytes ([estimatedBytes]); Firestore's limit is 1 MiB
     * per document, this leaves a fifth of it free.
     */
    const val MAX_COURSE_BYTES = 800_000
    /** Quiz attempts per person, course and day (Warsaw), checked by submitCourse. */
    const val MAX_ATTEMPTS_PER_DAY = 5

    /**
     * The framework programmes' minimum length in lesson hours (załącznik nr 1): general
     * instruction at least 3, periodic training of administrative and office staff at least 8
     * (other groups more: the manager sets the real length).
     */
    fun minLessonHours(kind: CourseKind): Int = when (kind) {
        CourseKind.GENERAL -> 0
        CourseKind.OHS_GENERAL -> 3
        CourseKind.OHS_PERIODIC -> 8
    }

    /** Whether [url] is an https address the app may open (videos are external links). */
    fun isHttpsUrl(url: String?): Boolean {
        val value = url?.trim() ?: return false
        if (value.length > MAX_URL || !value.startsWith("https://", ignoreCase = true)) return false
        val host = value.substring(8).substringBefore('/').substringBefore('?').substringBefore('#')
        return host.isNotEmpty() && '.' in host && host.none { it.isWhitespace() || it == '@' }
    }

    private fun utf8(value: String?): Int = value?.let { v ->
        var bytes = 0
        for (c in v) bytes += when {
            c.code < 0x80 -> 1
            c.code < 0x800 -> 2
            c.isSurrogate() -> 2 // half of a 4-byte character
            else -> 3
        }
        bytes
    } ?: 0

    /**
     * About how many bytes [course] takes in Firestore: its texts in UTF-8 (the stored lesson
     * JSON included) and a little for each field name.
     */
    fun estimatedBytes(course: Course): Int {
        val pages = course.pages.sumOf { page ->
            val p = page.presentation
            200 + utf8(page.id) + utf8(page.title) + utf8(page.body) + utf8(page.imagePath) + utf8(page.url) +
                (page.rich?.let { utf8(RichText.encode(it)) } ?: 0) +
                (p?.let { 150 + utf8(it.link) + utf8(it.embedUrl) + utf8(it.filePath) + utf8(it.fileName) + utf8(it.fileType) + utf8(it.pdfPath) + utf8(it.pdfName) } ?: 0)
        }
        val questions = course.questions.sumOf { q -> 60 + utf8(q.id) + utf8(q.text) + q.options.sumOf { 2 + utf8(it) } }
        return 600 + utf8(course.title) + utf8(course.description) + course.roles.sumOf { 2 + utf8(it) } + pages + questions
    }

    /**
     * [page] as it is stored (SH-211): the lesson sanitised; without any formatting it is plain
     * text again ([CoursePage.body] only, as before). With formatting (or on a presentation page)
     * [CoursePage.rich] keeps it and [CoursePage.body] its plain-text version for older app
     * versions; on a presentation page that names the link or the file too.
     */
    fun forStorage(page: CoursePage): CoursePage {
        val presentation = page.presentation?.takeIf { page.kind == CoursePageKind.PRESENTATION }
        val doc = RichText.sanitize(page.document)
        val keepRich = RichText.isFormatted(doc) || presentation != null
        val plain = if (keepRich) RichText.plainText(doc) else doc.text
        val pointer = presentation?.let { it.link ?: it.fileName }
        val body = if (pointer == null) plain else listOf(plain.trimEnd(), pointer).filter { it.isNotEmpty() }.joinToString("\n\n")
        return page.copy(
            rich = if (keepRich) doc else null,
            body = body.take(RichText.MAX_PLAIN),
            presentation = presentation
        )
    }

    /** Everything wrong with [course] and its answer [key] for saving; publishing also needs [forPublish]. */
    fun problems(course: Course, key: Map<String, List<Int>>, forPublish: Boolean = false): Set<CourseProblem> = buildSet {
        val title = course.title.trim()
        if (title.isEmpty()) add(CourseProblem.MISSING_TITLE)
        if (title.length > MAX_TITLE || course.description.length > MAX_DESCRIPTION) add(CourseProblem.TITLE_TOO_LONG)
        if (course.pages.size > MAX_PAGES) add(CourseProblem.TOO_MANY_PAGES)
        if (forPublish && course.pages.isEmpty()) add(CourseProblem.NO_PAGES)
        course.pages.forEach { page ->
            val rich = page.rich
            val textTooLong = if (rich != null) RichText.tooLarge(rich) || page.body.length > RichText.MAX_PLAIN else page.body.length > MAX_BODY
            if (page.title.length > MAX_PAGE_TITLE || textTooLong) add(CourseProblem.PAGE_TOO_LONG)
            if (page.kind == CoursePageKind.VIDEO_LINK && !isHttpsUrl(page.url)) add(CourseProblem.BAD_VIDEO_LINK)
            if (page.kind == CoursePageKind.IMAGE && page.imagePath.isNullOrBlank()) add(CourseProblem.MISSING_IMAGE)
            if (page.kind == CoursePageKind.PRESENTATION) {
                val presentation = page.presentation
                val linkOk = presentation == null || presentation.isUpload || isHttpsUrl(presentation.link)
                if (presentation?.isComplete != true || !linkOk) add(CourseProblem.BAD_PRESENTATION)
            }
        }
        if (estimatedBytes(course) > MAX_COURSE_BYTES) add(CourseProblem.COURSE_TOO_LARGE)
        if (course.questions.size > MAX_QUESTIONS) add(CourseProblem.TOO_MANY_QUESTIONS)
        course.questions.forEach { q ->
            if (q.text.isBlank() || q.text.length > MAX_QUESTION) add(CourseProblem.QUESTION_WITHOUT_TEXT)
            if (q.options.size !in MIN_OPTIONS..MAX_OPTIONS || q.options.any { it.isBlank() || it.length > MAX_OPTION }) {
                add(CourseProblem.BAD_OPTIONS)
            }
            val right = key[q.id].orEmpty()
            if (right.isEmpty() || right.any { it !in q.options.indices } || right.toSet().size != right.size ||
                (!q.multi && right.size != 1)
            ) {
                add(CourseProblem.NO_RIGHT_ANSWER)
            }
        }
        if (course.passPercent !in 50..100) add(CourseProblem.BAD_PASS_PERCENT)
        if (course.dueDays !in 0..MAX_DUE_DAYS) add(CourseProblem.BAD_DUE_DAYS)
        if (course.kind.isOhs) {
            if (course.questions.isEmpty()) add(CourseProblem.OHS_NEEDS_QUIZ)
            val ohs = course.ohs
            if (ohs == null || ohs.instructorName.isBlank()) add(CourseProblem.OHS_NEEDS_INSTRUCTOR)
            if ((ohs?.lessonHours ?: 0) < minLessonHours(course.kind)) add(CourseProblem.OHS_TOO_SHORT)
        }
    }
}

/**
 * Grading, the same as functions/courses.js (the server's is what counts; the app uses this for
 * the editor's preview and tests). A question is right when exactly its right options are picked.
 */
object CourseGrading {
    data class Grade(val score: Int, val passed: Boolean, val wrongQuestionIds: List<String>)

    /** [answers]: question id → picked option indexes. Unanswered questions are wrong. */
    fun grade(questions: List<QuizQuestion>, key: Map<String, List<Int>>, answers: Map<String, List<Int>>, passPercent: Int): Grade {
        if (questions.isEmpty()) return Grade(100, true, emptyList())
        val wrong = questions.filter { q -> answers[q.id].orEmpty().toSet() != key[q.id].orEmpty().toSet() || answers[q.id].isNullOrEmpty() }
            .map { it.id }
        val right = questions.size - wrong.size
        // Whole percent, rounded down, so 4 of 5 is 80 and 2 of 3 is 66.
        val score = right * 100 / questions.size
        return Grade(score, score >= passPercent, wrong)
    }

    /** Whether every question has an answer (the quiz cannot be sent before). */
    fun allAnswered(questions: List<QuizQuestion>, answers: Map<String, List<Int>>): Boolean =
        questions.all { !answers[it.id].isNullOrEmpty() }
}

// --- Who takes what ------------------------------------------------------------------------------

/** A course a person should take, with its due day (null: no deadline known). */
data class AssignedCourse(
    val course: Course,
    val dueOn: LocalDate?,
    /** A manager asked for it personally (else: by position or as a new hire). */
    val explicit: Boolean
)

object CourseAssignments {
    /**
     * The published courses [userId] (working with [engagement]) should take: assigned personally
     * ([explicit]: course id → assignment), by position, or as someone who started after the course
     * was published ([Course.allNewHires]). Due: the assignment's day, else [Course.dueDays] after
     * the later of the publication and the start of work.
     */
    fun forPerson(
        courses: List<Course>,
        userId: String,
        engagement: Engagement?,
        explicit: Map<String, CourseAssignment>
    ): List<AssignedCourse> {
        if (engagement == null || !engagement.isActive) return emptyList()
        val role = engagement.role?.trim()?.lowercase()
        val start = runCatching { LocalDate.parse(engagement.startDate.take(10)) }.getOrNull()
        return courses.filter { it.isPublished }.mapNotNull { course ->
            val assignment = explicit[course.id]?.takeIf { it.userId == userId }
            if (assignment != null) return@mapNotNull AssignedCourse(course, assignment.dueOn, explicit = true)
            val published = course.publishedAt?.let { LocalDate.parse(it.toString().take(10)) }
            val byRole = role != null && course.roles.any { it.trim().lowercase() == role }
            val newHire = course.allNewHires && start != null && published != null && start >= published
            if (!byRole && !newHire) return@mapNotNull null
            val from = listOfNotNull(published, start).maxOrNull()
            AssignedCourse(course, from?.plus(DatePeriod(days = course.dueDays)), explicit = false)
        }
    }
}

/** Where a person stands with the courses that count for readiness. */
enum class TrainingState {
    /** All done. */
    DONE,
    /** Some not done yet, none past its day. */
    PENDING,
    /** At least one not done after its day. */
    OVERDUE
}

/** workplaces/{wpId}/training_status/{uid} (server only, functions/courses.js). */
data class TrainingStatus(
    val userId: String,
    val workplaceId: String,
    val state: TrainingState,
    val done: Int,
    val pending: Int,
    /** The earliest due day of the courses not done. */
    val dueOn: LocalDate? = null
)

object TrainingStatuses {
    /**
     * [assigned] (the courses that count) against the passed [completions] (course id → passed)
     * on [today]; null when nothing counts.
     */
    fun of(userId: String, workplaceId: String, assigned: List<AssignedCourse>, passed: Set<String>, today: LocalDate): TrainingStatus? {
        val counting = assigned.filter { it.course.countsForReadiness || it.course.kind.isOhs }
        if (counting.isEmpty()) return null
        val open = counting.filter { it.course.id !in passed }
        val dueOn = open.mapNotNull { it.dueOn }.minOrNull()
        val state = when {
            open.isEmpty() -> TrainingState.DONE
            dueOn != null && dueOn < today -> TrainingState.OVERDUE
            else -> TrainingState.PENDING
        }
        return TrainingStatus(userId, workplaceId, state, counting.size - open.size, open.size, dueOn)
    }
}

// --- The OHS record ------------------------------------------------------------------------------

/**
 * The position groups of § 14 ust. 2 that decide the periodic training: its form, how often, and
 * when the first one is due (§ 15).
 */
enum class OhsWorkerGroup {
    /** Blue-collar positions (stanowiska robotnicze): periodic training as instruction, every 3 years. */
    BLUE_COLLAR,
    /** Employers and people managing staff: every 5 years, the first within 6 months. */
    MANAGERS,
    /** Administrative and office positions: every 6 years. */
    ADMIN_OFFICE,
    /** Engineering and technical, OHS service, others exposed to hazards: every 5 years. */
    OTHER;

    /** Guided self-study is not allowed for blue-collar positions (§ 15 ust. 1 and 2). */
    val maySelfStudy: Boolean get() = this != BLUE_COLLAR

    /** "Nie rzadziej niż raz na" (§ 15 ust. 1 and 2). */
    val periodicYears: Int
        get() = when (this) {
            BLUE_COLLAR -> 3
            ADMIN_OFFICE -> 6
            MANAGERS, OTHER -> 5
        }

    /** The first periodic training is due within this many months of starting (§ 15 ust. 4). */
    val firstPeriodicMonths: Int get() = if (this == MANAGERS) 6 else 12
}

enum class OhsGeneralMode {
    /** Taken in the app ([CourseKind.OHS_GENERAL]). */
    ONLINE,
    /** Conducted in person; the manager records it, the employee confirms it. */
    IN_PERSON
}

enum class OhsPeriodicMode {
    /** Guided self-study in the app ([CourseKind.OHS_PERIODIC]), then the exam. */
    SELF_STUDY,
    /** A course, seminar or instruction elsewhere; the manager records the certificate. */
    IN_PERSON
}

/** Instruktaż ogólny. */
data class OhsGeneralPart(
    val mode: OhsGeneralMode,
    /** The person who conducted it (§ 10 ust. 2). */
    val conductedBy: String,
    val completedOn: LocalDate,
    val courseId: String? = null,
    val courseVersion: Int? = null,
    val courseTitle: String? = null,
    val score: Int? = null,
    /** The employee's electronic confirmation (§ 12 ust. 1): the server's time. */
    val employeeConfirmedAt: Instant? = null,
    val recordedByName: String? = null
)

/** Instruktaż stanowiskowy: at the workplace, only recorded here. */
data class OhsStationPart(
    val position: String,
    val conductedBy: String,
    val conductedOn: LocalDate,
    /** Passed the test (sprawdzian, § 11 ust. 6) and admitted to work on the position. */
    val admittedOn: LocalDate,
    /** The head of the unit who admitted the person (recorded it). */
    val recordedByName: String? = null,
    val recordedAt: Instant? = null,
    val employeeConfirmedAt: Instant? = null
)

/** The periodic training. */
data class OhsPeriodicPart(
    val mode: OhsPeriodicMode,
    val courseId: String? = null,
    val courseVersion: Int? = null,
    val courseTitle: String? = null,
    val score: Int? = null,
    /** The self-study test was passed (the written part of the exam). */
    val testPassedOn: LocalDate? = null,
    /** The exam before the commission (§ 16 ust. 2). */
    val examOn: LocalDate? = null,
    val commission: String? = null,
    /** The number in the register of issued certificates (§ 5 pkt 6). */
    val certificateNumber: String? = null,
    val organizer: String? = null,
    val recordedByName: String? = null
)

/** What a manager records about a person's OHS training (recordOhsTraining). */
sealed interface OhsEntry {
    /** The position group and whether the person needs a workplace instruction (§ 11 ust. 1). */
    data class Settings(val group: OhsWorkerGroup, val stationRequired: Boolean) : OhsEntry
    data class GeneralInPerson(val conductedBy: String, val completedOn: LocalDate) : OhsEntry
    data class Station(val position: String, val conductedBy: String, val conductedOn: LocalDate, val admittedOn: LocalDate) : OhsEntry
    /** The exam after self-study in the app. Blank [certificateNumber]: the next one from the register. */
    data class PeriodicExam(val examOn: LocalDate, val commission: String, val certificateNumber: String = "") : OhsEntry
    /** Periodic training done elsewhere, with its certificate. */
    data class PeriodicElsewhere(val examOn: LocalDate, val organizer: String, val certificateNumber: String) : OhsEntry
}

/** The parts a person confirms themselves (§ 12 ust. 1). */
enum class OhsConfirmation { GENERAL, STATION }

/** The OHS dates the record gives (users/{uid}/hr/{wpId}.ohsTraining). */
data class OhsDates(val completedOn: LocalDate, val validUntil: LocalDate)

/** workplaces/{wpId}/ohs_records/{uid}: server only, read by the person and the managers. */
data class OhsRecord(
    val userId: String,
    val workplaceId: String,
    val displayName: String? = null,
    val group: OhsWorkerGroup? = null,
    /** Unknown or yes: the conservative default (§ 11 ust. 1). */
    val stationRequired: Boolean = true,
    val general: OhsGeneralPart? = null,
    val station: OhsStationPart? = null,
    val periodic: OhsPeriodicPart? = null,
    val updatedAt: Instant? = null
) {
    /** The general instruction is done and the employee confirmed it. */
    val generalDone: Boolean get() = general?.employeeConfirmedAt != null

    /** The workplace instruction is done (or not needed) and confirmed. */
    val stationDone: Boolean get() = !stationRequired || station?.employeeConfirmedAt != null

    val initialDone: Boolean get() = generalDone && stationDone

    /** The exam was passed and a certificate issued (self-study also needs the passed test). */
    val periodicDone: Boolean
        get() = periodic?.let { p ->
            p.examOn != null && !p.certificateNumber.isNullOrBlank() && (p.mode == OhsPeriodicMode.IN_PERSON || p.testPassedOn != null)
        } == true

    /** What the person still has to confirm. */
    val pendingConfirmations: List<OhsConfirmation>
        get() = buildList {
            if (general != null && general.employeeConfirmedAt == null) add(OhsConfirmation.GENERAL)
            if (stationRequired && station != null && station.employeeConfirmedAt == null) add(OhsConfirmation.STATION)
        }
}

object OhsRules {
    /**
     * SH-244: whether someone with [permission] at a workplace takes the initial OHS training
     * (the karta szkolenia wstępnego: general and workplace instruction). It is the employer's duty
     * towards the people working for them (Kodeks pracy art. 237³ § 2, § 8-12 of the regulation);
     * the owner is the employer, so owners are never listed, prompted or recorded for it. Employers
     * do take the periodic training (§ 14, § 15), which is recorded for owners too. Managers and
     * employees take both, whatever their position.
     */
    fun appliesTo(permission: UserPermission?): Boolean = permission != UserPermission.OWNER

    /** [appliesTo] for [user]'s engagement at [workplaceId] (no engagement: not the owner there). */
    fun appliesTo(user: User, workplaceId: String): Boolean = appliesTo(user.engagements[workplaceId]?.permission)

    /**
     * The OHS dates once every legally required part is done, else null (the date is never set
     * early): without a finished initial training (general and, when required, workplace
     * instruction, both confirmed) nothing. After it, the periodic training's certificate gives
     * [OhsWorkerGroup.periodicYears] from the exam; without one, the initial training is valid
     * until the first periodic training is due ([OhsWorkerGroup.firstPeriodicMonths] counted from
     * the initial training, which is never later than the start of work, so the day is never
     * late). An unknown group counts as the strictest: blue-collar periods, 12 months for the first.
     */
    fun dates(record: OhsRecord): OhsDates? {
        if (!record.initialDone) return null
        val group = record.group ?: OhsWorkerGroup.BLUE_COLLAR
        val periodic = record.periodic
        if (record.periodicDone && periodic?.examOn != null) {
            val exam = periodic.examOn
            return OhsDates(exam, exam.plus(DatePeriod(years = group.periodicYears)).minus(DatePeriod(days = 1)))
        }
        val initial = listOfNotNull(
            record.general?.completedOn,
            record.station?.admittedOn?.takeIf { record.stationRequired }
        ).maxOrNull() ?: return null
        return OhsDates(initial, initial.plus(DatePeriod(months = group.firstPeriodicMonths)).minus(DatePeriod(days = 1)))
    }
}

/** Course pictures in Storage (storage.rules: managers write, members read). */
object CourseFiles {
    const val MAX_IMAGE_BYTES: Int = 2 * 1024 * 1024
    val IMAGE_TYPES: Set<String> = setOf(DocumentFiles.JPEG, DocumentFiles.PNG)

    fun imagePath(workplaceId: String, courseId: String, imageId: String): String {
        listOf(workplaceId, courseId, imageId).forEach { require(it.isNotBlank() && '/' !in it) { "Invalid path segment" } }
        return "workplaces/$workplaceId/courses/$courseId/$imageId"
    }

    /** What is wrong with [bytes] as a course picture, or null. */
    fun imageProblem(bytes: ByteArray): DocumentProblem? = when {
        bytes.isEmpty() -> DocumentProblem.EMPTY
        bytes.size > MAX_IMAGE_BYTES -> DocumentProblem.TOO_LARGE
        DocumentFiles.sniff(bytes) !in IMAGE_TYPES -> DocumentProblem.UNSUPPORTED_TYPE
        else -> null
    }
}

/** A course with the reader's own progress and result (the employee's list). */
data class MyCourse(
    val assigned: AssignedCourse,
    val progress: CourseProgress? = null,
    val completion: CourseCompletion? = null
) {
    val course: Course get() = assigned.course
    val passed: Boolean get() = completion?.passed == true
    fun overdueOn(today: LocalDate): Boolean = !passed && assigned.dueOn?.let { it < today } == true
}

/** One person's row in a course's progress report (managers). */
data class CourseProgressRow(
    val userId: String,
    val displayName: String,
    val dueOn: LocalDate?,
    val pagesSeen: Int,
    val completion: CourseCompletion?,
    /** The person no longer works here (a result kept from before). */
    val former: Boolean = false
) {
    val passed: Boolean get() = completion?.passed == true
    fun overdueOn(today: LocalDate): Boolean = !passed && dueOn?.let { it < today } == true
}

/** Everyone a course is for, with their progress (managers). */
data class CourseProgressReport(
    val course: Course,
    val rows: List<CourseProgressRow>
) {
    val done: Int get() = rows.count { it.passed && !it.former }
    val total: Int get() = rows.count { !it.former }
}
